import { usePageMeta } from './hooks/usePageMeta';
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import { useAuthContext } from './context/AuthProvider';
import { Toaster } from 'react-hot-toast';
import Header from './components/header';
import SplashPage from './pages/SplashPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import { AuthProvider } from './context/AuthProvider';

/**
 * Lazy import that self-heals a STALE-BUILD chunk 404. When a new version is
 * deployed, an open tab still references the OLD chunk hashes; navigating to a
 * lazy route then requests a chunk that no longer exists (404 → "Loading chunk
 * failed"). Rather than crash to the error boundary, reload ONCE to pick up the
 * fresh index.html + chunks. A 15s guard prevents a reload loop if the failure
 * is something other than a stale build.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a generic lazy wrapper must accept components with any props shape
function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      try {
        const KEY = 'vmt-chunk-reload-at';
        const last = Number(window.sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 15000) {
          window.sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return await new Promise<{ default: T }>(() => { /* hang until reload */ });
        }
      } catch { /* sessionStorage unavailable — fall through to rethrow */ }
      throw err;
    }
  });
}

/**
 * Post-login landing router. Sends each user to a home they can actually use, so
 * a basic employee never lands on (or is bounced off) an admin-only dashboard:
 *   - store/orders admins        -> /admin/dashboard
 *   - supervisors (event ops)    -> /admin/ops
 *   - everyone else (employees)  -> /admin/inbox (their My Work start)
 * Login and the "not authorized" fallback both point here instead of a fixed
 * admin page, so nobody hits a 403 immediately after signing in.
 */
const AdminLanding: React.FC = () => {
  const { user, loading, hasPerm, hasRole } = useAuthContext();
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  // Store admins → the merchandise & orders console. Gate ONLY on `merchandise`
  // (which is what /admin/dashboard requires): event_admin/supervisor hold `orders`
  // but NOT `merchandise`, so sending them here bounced them onto a 403 that looped
  // back through this landing. They fall through to /admin/home below.
  if (hasPerm('merchandise')) return <Navigate to="/admin/dashboard" replace />;
  // Employee rank and up (supervisors, event/org admins without store perms, and
  // basic employees) → the role-adaptive home (admins see the org overview,
  // employees see their personal "my work" view).
  if (hasRole('employee')) return <Navigate to="/admin/home" replace />;
  // scanner_only (below employee rank) → straight to the gate scanner.
  if (hasPerm('scanner')) return <Navigate to="/admin/ticket-scanner" replace />;
  // No recognized access → the home route's guard shows the "not provisioned"
  // message (fail-safe; a properly-provisioned account never reaches this).
  return <Navigate to="/admin/home" replace />;
};

// Lazy-loaded page components (code splitting)
const Tickets = lazyWithRetry(() => import('./pages/Tickets'));
const Merchandise = lazyWithRetry(() => import('./pages/Merchandise'));
const TeamPage = lazyWithRetry(() => import('./pages/TeamPage'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout'));
const Sponsor = lazyWithRetry(() => import('./pages/Sponsor'));
const BecomeSponsor = lazyWithRetry(() => import('./pages/BecomeSponsor'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const AccessibilityPage = lazyWithRetry(() => import('./pages/AccessibilityPage'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const DisclaimerPage = lazyWithRetry(() => import('./pages/legal/DisclaimerPage'));
const TicketPolicyPage = lazyWithRetry(() => import('./pages/legal/TicketPolicyPage'));
const FanCodeOfConductPage = lazyWithRetry(() => import('./pages/legal/FanCodeOfConductPage'));
const AlumniPage = lazyWithRetry(() => import('./pages/alumni'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const PlayerManager = lazyWithRetry(() => import('./pages/PlayerManager'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const CheckoutSuccess = lazyWithRetry(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazyWithRetry(() => import('./pages/CheckoutCancel'));
const SponsorManager = lazyWithRetry(() => import('./pages/SponsorManager'));
const PressReleaseManager = lazyWithRetry(() => import('./pages/PressReleaseManager'));
const NotificationManager = lazyWithRetry(() => import('./pages/NotificationManager'));
const SettingsManager = lazyWithRetry(() => import('./pages/SettingsManager'));
const NewsPage = lazyWithRetry(() => import('./pages/NewsPage'));
const PressReleaseDetail = lazyWithRetry(() => import('./pages/PressReleaseDetail'));
const StatsPage = lazyWithRetry(() => import('./pages/StatsPage'));
const TicketScanner = lazyWithRetry(() => import('./pages/TicketScanner'));
const Schedule = lazyWithRetry(() => import('./pages/Schedule'));
const RevenueAnalytics = lazyWithRetry(() => import('./pages/RevenueAnalytics'));
const MarketingManager = lazyWithRetry(() => import('./pages/MarketingManager'));
const Unsubscribe = lazyWithRetry(() => import('./pages/Unsubscribe'));
const Activate = lazyWithRetry(() => import('./pages/Activate'));
const VolunteerActivate = lazyWithRetry(() => import('./pages/VolunteerActivate'));
const EmployeesManager = lazyWithRetry(() => import('./pages/EmployeesManager'));
const EmployeeOpsSetup = lazyWithRetry(() => import('./pages/EmployeeOpsSetup'));
const TimeClock = lazyWithRetry(() => import('./pages/TimeClock'));
const TimeAdmin = lazyWithRetry(() => import('./pages/TimeAdmin'));
const EmployeeTasks = lazyWithRetry(() => import('./pages/EmployeeTasks'));
const TaskAdmin = lazyWithRetry(() => import('./pages/TaskAdmin'));
const InventoryCount = lazyWithRetry(() => import('./pages/InventoryCount'));
const InventoryAdmin = lazyWithRetry(() => import('./pages/InventoryAdmin'));
const EmployeeDashboard = lazyWithRetry(() => import('./pages/EmployeeDashboard'));
const OpsDashboard = lazyWithRetry(() => import('./pages/OpsDashboard'));
const NotificationsCenter = lazyWithRetry(() => import('./pages/NotificationsCenter'));
const HelpGuide = lazyWithRetry(() => import('./pages/HelpGuide'));
const ReportsPage = lazyWithRetry(() => import('./pages/ReportsPage'));
const StaffingAdmin = lazyWithRetry(() => import('./pages/StaffingAdmin'));
const ScannerSetup = lazyWithRetry(() => import('./pages/ScannerSetup'));
const DeliveryIssues = lazyWithRetry(() => import('./pages/DeliveryIssues'));
const StaffAvailability = lazyWithRetry(() => import('./pages/StaffAvailability'));

// Styled 404 (was bare unstyled text) with a route home; noindex so
// crawlers don't file unknown URLs as real pages.
const NotFoundPage = () => {
  usePageMeta('Page Not Found', 'This page does not exist on breckvipers.com.', { noindex: true });
  return (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '2rem',
      gap: '0.75rem',
    }}
  >
    <div style={{ fontSize: '3.5rem', fontWeight: 800, color: '#d32f2f', lineHeight: 1 }}>404</div>
    <h1 style={{ margin: 0, fontSize: '1.4rem', color: '#1a1a1a' }}>Page not found</h1>
    <p style={{ margin: 0, color: '#555', maxWidth: 460 }}>
      The page you're looking for doesn't exist or may have moved.
    </p>
    <Link
      to="/"
      style={{
        marginTop: '0.5rem',
        padding: '0.6rem 1.25rem',
        background: '#d32f2f',
        color: '#fff',
        borderRadius: 8,
        textDecoration: 'none',
        fontWeight: 600,
      }}
    >
      Back to home
    </Link>
  </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
      <div className="App">
        <Toaster />
        <Header />
        <AppErrorBoundary>
        <Suspense fallback={<div className="page-loading"><div className="loading-spinner"></div></div>}>
        <Routes>
          {/* Existing Pages */}
          <Route path="/" element={<SplashPage />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/merchandise" element={<Merchandise />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/sponsor" element={<Sponsor />} />
          <Route path="/become-a-sponsor" element={<BecomeSponsor />} />
          <Route path="/alumni" element={<AlumniPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
          <Route path="/legal/ticket-policy" element={<TicketPolicyPage />} />
          <Route path="/legal/fan-code-of-conduct" element={<FanCodeOfConductPage />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<Login />} />
          {/* Public invitation activation (NOT protected) */}
          <Route path="/admin/activate" element={<Activate />} />
          {/* Public day-of volunteer activation (NOT protected) */}
          <Route path="/volunteer/activate" element={<VolunteerActivate />} />
          <Route
            path="/admin/employees"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="employees">
                <EmployeesManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/setup"
            element={
              // AUTH-ONLY BY DESIGN: this is the one-time RBAC bootstrap that MINTS
              // claims (migrateAdminUsers). Gating it behind minRole="super_admin"
              // is a chicken-and-egg lockout — a claimless admin could never reach
              // the page that grants the claims. The server callable enforces the
              // real bootstrap authorization (legacy adminUsers super_admin / 'all'
              // / owner email), so auth-only here is safe.
              <ProtectedRoute>
                <EmployeeOpsSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/help"
            element={
              <ProtectedRoute>
                <HelpGuide />
              </ProtectedRoute>
            }
          />
          {/* Role-aware post-login landing: routes each user to a home they can
              actually use (store admins → dashboard, everyone else → adaptive
              /admin/home). Login + the "not authorized" fallback point here so a
              basic employee never lands on, or bounces off, an admin page. */}
          <Route path="/admin" element={<AdminLanding />} />
          {/* /admin/dashboard is the store/orders console — gated to the
              'merchandise' permission so a basic employee can't reach it by URL
              (matches its nav item). Server rules remain the data-layer backstop. */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requirePerm="merchandise">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/admin/players" 
            element={
              <ProtectedRoute requirePerm="players">
                <PlayerManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/sponsors" 
            element={
              <ProtectedRoute requirePerm="sponsors">
                <SponsorManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/press-releases" 
            element={
              <ProtectedRoute requirePerm="press">
                <PressReleaseManager />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute requirePerm="notifications">
                <NotificationManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute minRole="org_admin" requirePerm="settings">
                <SettingsManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ticket-scanner"
            element={
              <ProtectedRoute requirePerm="scanner">
                <TicketScanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/revenue"
            element={
              <ProtectedRoute minRole="org_admin" requirePerm="orders">
                <RevenueAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/marketing"
            element={
              <ProtectedRoute minRole="org_admin" requirePerm="email">
                <MarketingManager />
              </ProtectedRoute>
            }
          />

          {/* Employee-Ops Phase 3 — Time Tracking */}
          <Route
            path="/admin/time"
            element={
              <ProtectedRoute minRole="employee">
                <TimeClock />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/time-admin"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="staffing">
                <TimeAdmin />
              </ProtectedRoute>
            }
          />

          {/* Employee-Ops Phase 4 — Task Management */}
          <Route
            path="/admin/tasks"
            element={
              <ProtectedRoute minRole="employee">
                <EmployeeTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks-admin"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="staffing">
                <TaskAdmin />
              </ProtectedRoute>
            }
          />

          {/* Employee-Ops Phase 5 — Inventory */}
          <Route
            path="/admin/inventory"
            element={
              <ProtectedRoute minRole="employee">
                <InventoryCount />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/inventory-admin"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="inventory">
                <InventoryAdmin />
              </ProtectedRoute>
            }
          />

          {/* Employee-Ops Phase 7 — Dashboards, Notifications, Reporting */}
          <Route
            path="/admin/home"
            element={
              <ProtectedRoute minRole="employee">
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/inbox"
            element={
              <ProtectedRoute minRole="employee">
                <NotificationsCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ops"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="staffing">
                <OpsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute minRole="supervisor" requirePerm="reports">
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/delivery-issues"
            element={
              <ProtectedRoute minRole="org_admin" requirePerm="orders">
                <DeliveryIssues />
              </ProtectedRoute>
            }
          />

          {/* Employee-Ops Phase 2 — Events, Staffing, Availability, Assignments */}
          <Route
            path="/admin/staffing"
            element={
              <ProtectedRoute minRole="event_admin" requirePerm="staffing">
                <StaffingAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/scanner-setup"
            element={
              <ProtectedRoute minRole="event_admin" requirePerm="staffing">
                <ScannerSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/availability"
            element={
              <ProtectedRoute minRole="employee">
                <StaffAvailability />
              </ProtectedRoute>
            }
          />

          {/* News Routes */}
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<PressReleaseDetail />} />

          {/* Public unsubscribe (CAN-SPAM) */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          
          {/* Schedule Page */}
          <Route path="/schedule" element={<Schedule />} />

          {/* Legacy / nav-referenced paths that map onto a real page → redirect
              so an old link or typed URL lands somewhere useful instead of a
              bare "under construction" stub. Paths with no real equivalent were
              removed entirely so they fall through to the catch-all 404 below. */}
          <Route path="/roster" element={<Navigate to="/team" replace />} />
          <Route path="/staff" element={<Navigate to="/team" replace />} />
          <Route path="/tickets/single-game" element={<Navigate to="/tickets" replace />} />
          <Route path="/tickets/season-tickets" element={<Navigate to="/tickets" replace />} />
          <Route path="/tickets/group-packages" element={<Navigate to="/tickets" replace />} />
          <Route path="/tickets/premium-seating" element={<Navigate to="/tickets" replace />} />
          <Route path="/tickets/game/:id" element={<Navigate to="/tickets" replace />} />
          <Route path="/legal/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/legal/accessibility" element={<Navigate to="/accessibility" replace />} />

          {/* Checkout routes */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />

          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </AppErrorBoundary>
      </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
